import { ROOT_API_URL } from "../config/apiConfig";

/**
 * Fetch ports from the API
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} - Array of ports
 */
export const fetchPorts = async (token) => {
  try {
    const response = await fetch(`${ROOT_API_URL}/api/ports`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch ports');
    }

    const data = await response.json();
    
    if (data.success && data.data?.ports) {
      return data.data.ports;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching ports:', error);
    throw error;
  }
};

/**
 * Fetch cabins from the API based on type
 * @param {string} token - Authorization token
 * @param {string} type - Cabin type (passenger, vehicle, cargo)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Array>} - Array of cabins
 */
export const fetchCabins = async (token, type = 'passenger', page = 1, limit = 10) => {
  try {
    const response = await fetch(
      `${ROOT_API_URL}/api/cabins?type=${type}&page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch cabins');
    }

    const data = await response.json();
    
    if (data.success && data.data?.cabins) {
      return data.data.cabins;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching cabins:', error);
    throw error;
  }
};

/**
 * Fetch payload types from the API based on category
 * @param {string} token - Authorization token
 * @param {string} category - Category type (passenger, vehicle, cargo)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Array>} - Array of payload types
 */
export const fetchPayloadTypes = async (token, category = 'passenger', page = 1, limit = 100) => {
  try {
    const response = await fetch(
      `${ROOT_API_URL}/api/payload-types?page=${page}&limit=${limit}&category=${category}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch payload types');
    }

    const data = await response.json();
    
    if (data.success && data.data?.payloadTypes) {
      return data.data.payloadTypes;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching payload types:', error);
    throw error;
  }
};

/**
 * Search trips from the API
 * @param {string} token - Authorization token
 * @param {Object} searchParams - Search parameters
 * @param {string} searchParams.category - Category (passenger, vehicle, cargo)
 * @param {string} searchParams.tripType - Trip type (one_way, return)
 * @param {string} searchParams.originPort - Origin port ID
 * @param {string} searchParams.destinationPort - Destination port ID
 * @param {string} searchParams.departureDate - Departure date
 * @param {string} [searchParams.returnDate] - Return date (optional for return trips)
 * @param {string} searchParams.cabin - Cabin ID
 * @param {string} searchParams.visaType - Visa type
 * @param {Array} searchParams.passengers - Array of passenger payload types with quantities
 * @returns {Promise<Object>} - Trip search results
 */
export const searchTrips = async (token, searchParams) => {
  try {
    const isB2C = token === localStorage.getItem("authToken") && !localStorage.getItem("companyAuthToken");
    const endpoint = isB2C ? `${ROOT_API_URL}/api/b2c/trip-search` : `${ROOT_API_URL}/api/trip-search`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(searchParams),
    });

    if (!response.ok) {
      throw new Error('Failed to search trips');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching trips:', error);
    throw error;
  }
};
